import { fetchUtils, HttpError, DataProvider } from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';

const API_URL = `${process.env.REACT_APP_API_URL}/api`;
const imgBasedResources = ['products', 'stock-movements', 'telegram-products'];
const attachmentBasedResources = ['manual-orders'];
const photosBasedResources = ['product-compositions'];
const CATALOG_LABELS_RESOURCE = 'admin/catalog-labels';
const CUSTOMIZATION_OPTIONS_RESOURCE = 'admin/customization-options';
const DELIVERY_TIME_SLOTS_RESOURCE = 'delivery-time-slots';
const DELIVERY_TIME_SLOTS_BACKEND = 'admin/delivery-time-slots';
const ATTRIBUTE_DEFINITIONS_RESOURCE = 'attribute-definitions';
const ATTRIBUTE_DEFINITIONS_BACKEND = 'admin/product-attribute-definitions';

const httpClient = (url: string, options: fetchUtils.Options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  const token = localStorage.getItem('authToken');
  (options.headers as Headers).set('Authorization', `Bearer ${token}`);
  return fetchUtils.fetchJson(url, options);
};

const backendRestProvider = simpleRestProvider(API_URL, httpClient);

interface UploadConfig {
  data: Record<string, unknown>;
  resource: string;
  method: 'create' | 'update';
  id?: string | number;
  fileFields: FileFieldConfig[];
  arrayFields?: string[];
  objectFields?: string[];
  nullableFields?: string[];
  requiredFields?: string[];
  updateHttpMethod?: 'PUT' | 'PATCH';
}

interface FileFieldConfig {
  sourceKey: string;
  formDataKey: string;
  existingKey: string;
  multiple: boolean;
}

function submitFormData(config: UploadConfig): Promise<Record<string, unknown>> {
  const {
    data,
    resource,
    method,
    id,
    fileFields,
    arrayFields = [],
    objectFields = [],
    nullableFields = [],
    requiredFields = [],
  } = config;

  const formData = new FormData();
  const skipKeys = new Set<string>();

  for (const ff of fileFields) {
    skipKeys.add(ff.sourceKey);
    const value = data[ff.sourceKey];
    if (!Array.isArray(value)) {
      if (value && typeof value === 'object' && (value as { rawFile?: File }).rawFile) {
        formData.append(ff.formDataKey, (value as { rawFile: File }).rawFile);
      }
      continue;
    }
    const existingUrls: string[] = [];
    for (const item of value) {
      if (item?.rawFile) {
        formData.append(ff.formDataKey, item.rawFile);
      } else if (typeof item === 'string') {
        existingUrls.push(item);
      } else if (item?.src && !item?.rawFile) {
        existingUrls.push(item.src);
      }
    }
    if (existingUrls.length > 0) {
      formData.append(ff.existingKey, JSON.stringify(existingUrls));
    }
  }

  for (const key of arrayFields) {
    skipKeys.add(key);
    const arr = data[key];
    if (Array.isArray(arr)) {
      formData.append(key, JSON.stringify(arr));
    }
  }

  for (const key of objectFields) {
    skipKeys.add(key);
    const obj = data[key];
    if (obj === null) {
      formData.append(key, '');
    } else if (obj !== undefined && typeof obj === 'object' && !Array.isArray(obj)) {
      formData.append(key, JSON.stringify(obj));
    }
  }

  for (const key of Object.keys(data)) {
    if (skipKeys.has(key)) continue;
    const v = data[key];
    if (v !== undefined && v !== null) {
      if (typeof v === 'object' && !(v instanceof Date) && !(v instanceof Blob)) continue;
      formData.append(key, v as string | Blob);
    } else if (nullableFields.includes(key)) {
      formData.append(key, '');
    } else if (requiredFields.includes(key) && method === 'create') {
      throw new HttpError(`Required field "${key}" is missing`, 400);
    }
  }

  const token = localStorage.getItem('authToken');
  const url = method === 'create'
    ? `${API_URL}/${resource}`
    : `${API_URL}/${resource}/${id}`;

  const httpMethod =
    method === 'create' ? 'POST' : config.updateHttpMethod ?? 'PUT';

  return fetch(url, {
    method: httpMethod,
    body: formData,
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(async (response) => {
      const json = await response.json();
      if (!response.ok) {
        const baseMessage = Array.isArray(json.message)
          ? json.message.join(', ')
          : json.message || json.error || 'An error occurred';
        const details =
          Array.isArray(json.errors) && json.errors.length > 0
            ? `: ${json.errors.join('; ')}`
            : '';
        throw new HttpError(`${baseMessage}${details}`, response.status, json);
      }
      return json;
    })
    .then((json) => ({ id: json.id, ...json }));
}

const uploadImage = (data: Record<string, unknown>, resource: string, method: 'create' | 'update', id?: string | number) =>
  submitFormData({
    data,
    resource,
    method,
    id,
    fileFields: [{ sourceKey: 'image', formDataKey: 'image', existingKey: 'existingImage', multiple: false }],
    objectFields: resource === 'products' ? ['translations'] : [],
  });

const CAMPAIGNS_RESOURCE = 'campaigns';

const stripCampaignReadOnly = (data: Record<string, unknown>) => {
  const {
    id: _id,
    createdAt: _c,
    updatedAt: _u,
    deletedAt: _d,
    image: _img,
    ...rest
  } = data;
  return rest;
};

const uploadCampaignImage = async (id: string | number, rawFile: File): Promise<Record<string, unknown>> => {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  formData.append('image', rawFile);
  const response = await fetch(`${API_URL}/${CAMPAIGNS_RESOURCE}/${id}/image`, {
    method: 'POST',
    body: formData,
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  if (!response.ok) {
    const message = Array.isArray(json.message)
      ? json.message.join(', ')
      : json.message || json.error || 'Image upload failed';
    throw new HttpError(message, response.status, json);
  }
  return json;
};

const extractCampaignImageFile = (data: Record<string, unknown>): File | undefined => {
  const img = data.image as { rawFile?: File } | undefined;
  return img && typeof img === 'object' && img.rawFile instanceof File ? img.rawFile : undefined;
};

const createCampaign = async (data: Record<string, unknown>) => {
  const rawFile = extractCampaignImageFile(data);
  const body = stripCampaignReadOnly(data);
  const { json } = await httpClient(`${API_URL}/${CAMPAIGNS_RESOURCE}`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
  });
  let record = json as Record<string, unknown>;
  if (rawFile && record?.id) {
    record = await uploadCampaignImage(record.id as string, rawFile);
  }
  return record;
};

const updateCampaign = async (id: string | number, data: Record<string, unknown>) => {
  const rawFile = extractCampaignImageFile(data);
  const body = stripCampaignReadOnly(data);
  const { json } = await httpClient(`${API_URL}/${CAMPAIGNS_RESOURCE}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
  });
  let record = json as Record<string, unknown>;
  if (rawFile) {
    record = await uploadCampaignImage(id, rawFile);
  }
  return record;
};

const uploadWithAttachments = (data: Record<string, unknown>, resource: string, method: 'create' | 'update', id?: string | number) =>
  submitFormData({
    data,
    resource,
    method,
    id,
    fileFields: [{ sourceKey: 'attachments', formDataKey: 'attachments', existingKey: 'existingAttachments', multiple: true }],
    arrayFields: ['items'],
    nullableFields: ['deliveryFullName', 'deliveryPhone', 'deliveryAddress', 'leadContact', 'comment', 'fulfillTimeStart', 'fulfillTimeEnd', 'clientId'],
    requiredFields: ['branchId'],
  });

// Drop attributes the user left blank — a blank dimension/range field arrives as
// an empty object `{}` and must be omitted entirely rather than sent as junk.
export const pruneAttributes = (attributes: Record<string, unknown> | undefined | null) => {
  if (!attributes || typeof attributes !== 'object') return undefined;
  const cleaned: Record<string, unknown> = {};
  for (const [slug, value] of Object.entries(attributes)) {
    // Drop blank entries: null/undefined, a literally-empty {}, or an object
    // whose every value is blank (null/undefined/"") — e.g. a range input the
    // user left untouched serializes as {min:'',max:''} or {min:null,max:null}.
    // A partially-filled object (e.g. {min:10,max:''}) is kept so the backend
    // can reject it. Arrays are never treated as blank.
    const isBlank =
      value === null ||
      value === undefined ||
      (typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.values(value as Record<string, unknown>).every(
          (v) => v === null || v === undefined || v === '',
        ));
    if (isBlank) continue;
    cleaned[slug] = value;
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

const uploadWithPhotos = (data: Record<string, unknown>, resource: string, method: 'create' | 'update', id?: string | number) => {
  // Strip the backend-returned `labels` detail array — we only send `labelIds`.
  const { labels: _labels, ...rest } = data;
  const cleanedAttributes = pruneAttributes(rest.attributes as Record<string, unknown> | undefined | null);
  if (cleanedAttributes === undefined) {
    delete rest.attributes;
  } else {
    rest.attributes = cleanedAttributes;
  }
  return submitFormData({
    data: rest,
    resource,
    method,
    id,
    fileFields: [{ sourceKey: 'photos', formDataKey: 'photos', existingKey: 'existingPhotos', multiple: true }],
    arrayFields: [
      'compositionItems',
      'labelIds',
      'ingredients',
      'customizationOptionAdditions',
      'customizationOptionExclusions',
      'relatedCompositionIds',
    ],
    objectFields: ['translations', 'attributes'],
  });
};

const transformAttachmentsForEdit = (data: Record<string, unknown>) => {
  if (Array.isArray(data.attachments)) {
    data.attachments = (data.attachments as string[]).map((url, index) => ({
      src: url,
      title: `Attachment ${index + 1}`,
    }));
  }
  return data;
};

const transformPhotosForEdit = (data: Record<string, unknown>) => {
  if (Array.isArray(data.photos)) {
    data.photos = (data.photos as string[]).map((url, index) => ({
      src: url,
      title: `Photo ${index + 1}`,
    }));
  }
  // Prefill labelIds from the backend-returned labels array for ReferenceArrayInput.
  if (Array.isArray((data as any).labels) && data.labelIds === undefined) {
    (data as any).labelIds = ((data as any).labels as Array<{ id: string }>).map((l) => l.id);
  }
  return data;
};

interface CatalogLabel {
  id: string;
  name: string;
  slug: string;
  translations?: Record<string, string> | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const sortAndPaginate = <T extends Record<string, any>>(
  items: T[],
  sort: { field: string; order: 'ASC' | 'DESC' },
  pagination: { page: number; perPage: number },
): { data: T[]; total: number } => {
  const { field, order } = sort;
  const sorted = [...items].sort((a, b) => {
    const av = a?.[field];
    const bv = b?.[field];
    if (av === bv) return 0;
    if (av === undefined || av === null) return 1;
    if (bv === undefined || bv === null) return -1;
    if (av < bv) return order === 'ASC' ? -1 : 1;
    return order === 'ASC' ? 1 : -1;
  });
  const { page, perPage } = pagination;
  const start = (page - 1) * perPage;
  return { data: sorted.slice(start, start + perPage), total: sorted.length };
};

const fetchCatalogLabels = async (includeInactive: boolean): Promise<CatalogLabel[]> => {
  const url = includeInactive
    ? `${API_URL}/${CATALOG_LABELS_RESOURCE}?includeInactive=true`
    : `${API_URL}/${CATALOG_LABELS_RESOURCE}`;
  const { json } = await httpClient(url);
  return (json as CatalogLabel[]) || [];
};

const catalogLabelsProvider = {
  getList: async (params: {
    pagination: { page: number; perPage: number };
    sort: { field: string; order: 'ASC' | 'DESC' };
    filter: Record<string, any>;
  }) => {
    const includeInactive = params.filter?.includeInactive === true ||
      params.filter?.includeInactive === 'true';
    const all = await fetchCatalogLabels(includeInactive);
    const q: string | undefined = params.filter?.q;
    const filtered = q
      ? all.filter((l) => {
          const needle = q.toLowerCase();
          return (
            (l.name || '').toLowerCase().includes(needle) ||
            (l.slug || '').toLowerCase().includes(needle)
          );
        })
      : all;
    return sortAndPaginate(filtered, params.sort, params.pagination);
  },

  getOne: async (params: { id: string | number }) => {
    const { json } = await httpClient(`${API_URL}/${CATALOG_LABELS_RESOURCE}/${params.id}`);
    return { data: json };
  },

  getMany: async (params: { ids: Array<string | number> }) => {
    const all = await fetchCatalogLabels(true);
    const idSet = new Set(params.ids.map(String));
    return { data: all.filter((l) => idSet.has(String(l.id))) };
  },

  getManyReference: async (params: {
    pagination: { page: number; perPage: number };
    sort: { field: string; order: 'ASC' | 'DESC' };
    filter: Record<string, any>;
  }) => {
    const all = await fetchCatalogLabels(params.filter?.includeInactive === true);
    return sortAndPaginate(all, params.sort, params.pagination);
  },

  create: async (params: { data: Record<string, unknown> }) => {
    const { json } = await httpClient(`${API_URL}/${CATALOG_LABELS_RESOURCE}`, {
      method: 'POST',
      body: JSON.stringify(params.data),
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    });
    return { data: json };
  },

  update: async (params: { id: string | number; data: Record<string, unknown> }) => {
    // Backend uses PATCH and accepts partial payload. Strip server-managed fields.
    const { id: _id, createdAt: _c, updatedAt: _u, deletedAt: _d, ...patchBody } = params.data;
    const { json } = await httpClient(`${API_URL}/${CATALOG_LABELS_RESOURCE}/${params.id}`, {
      method: 'PATCH',
      body: JSON.stringify(patchBody),
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    });
    return { data: json };
  },

  updateMany: async (params: { ids: Array<string | number>; data: Record<string, unknown> }) => {
    const responses = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${CATALOG_LABELS_RESOURCE}/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(params.data),
          headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
        }),
      ),
    );
    return { data: responses.map((r) => (r.json as { id: string | number }).id) };
  },

  delete: async (params: { id: string | number }) => {
    const { json } = await httpClient(`${API_URL}/${CATALOG_LABELS_RESOURCE}/${params.id}`, {
      method: 'DELETE',
    });
    return { data: json };
  },

  deleteMany: async (params: { ids: Array<string | number> }) => {
    const responses = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${CATALOG_LABELS_RESOURCE}/${id}`, { method: 'DELETE' }),
      ),
    );
    return { data: responses.map((r) => (r.json as { id: string | number })?.id ?? '') };
  },
};

interface CustomizationOption {
  id: string;
  slug: string;
  name: string;
  type: string;
  price: number | string;
  photo?: string | null;
  translations?: Record<string, any> | null;
  isActive: boolean;
  isGlobal: boolean;
  labels?: Array<{ id: string; name: string; slug: string }>;
  labelIds?: string[];
  createdAt?: string;
  updatedAt?: string;
}

const transformCustomizationOption = (raw: CustomizationOption): CustomizationOption => {
  if (Array.isArray(raw.labels) && raw.labelIds === undefined) {
    raw.labelIds = raw.labels.map((l) => l.id);
  }
  return raw;
};

const hasCustomizationOptionImageUpload = (data: Record<string, unknown>): boolean => {
  const img = data.image as { rawFile?: File } | undefined;
  return !!img && typeof img === 'object' && img.rawFile instanceof File;
};

const fetchCustomizationOptions = async (params: {
  includeInactive?: boolean;
  labelId?: string;
  isGlobal?: boolean;
}): Promise<CustomizationOption[]> => {
  const search = new URLSearchParams();
  if (params.includeInactive) search.set('includeInactive', 'true');
  if (params.labelId) search.set('labelId', params.labelId);
  if (params.isGlobal !== undefined) search.set('isGlobal', String(params.isGlobal));
  const qs = search.toString();
  const url = qs
    ? `${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}?${qs}`
    : `${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}`;
  const { json } = await httpClient(url);
  const arr = (json as CustomizationOption[]) || [];
  return arr.map(transformCustomizationOption);
};

const customizationOptionsProvider = {
  getList: async (params: {
    pagination: { page: number; perPage: number };
    sort: { field: string; order: 'ASC' | 'DESC' };
    filter: Record<string, any>;
  }) => {
    const includeInactive = params.filter?.includeInactive === true ||
      params.filter?.includeInactive === 'true';
    const labelId = params.filter?.labelId as string | undefined;
    const isGlobal = params.filter?.isGlobal === true || params.filter?.isGlobal === 'true'
      ? true
      : params.filter?.isGlobal === false || params.filter?.isGlobal === 'false'
      ? false
      : undefined;
    const all = await fetchCustomizationOptions({ includeInactive, labelId, isGlobal });
    const q: string | undefined = params.filter?.q;
    const typeFilter: string | undefined = params.filter?.type;
    const filtered = all.filter((o) => {
      if (typeFilter && o.type !== typeFilter) return false;
      if (q) {
        const needle = q.toLowerCase();
        if (
          !(o.name || '').toLowerCase().includes(needle) &&
          !(o.slug || '').toLowerCase().includes(needle)
        ) {
          return false;
        }
      }
      return true;
    });
    return sortAndPaginate(filtered, params.sort, params.pagination);
  },

  getOne: async (params: { id: string | number }) => {
    const { json } = await httpClient(`${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}/${params.id}`);
    return { data: transformCustomizationOption(json as CustomizationOption) };
  },

  getMany: async (params: { ids: Array<string | number> }) => {
    const all = await fetchCustomizationOptions({ includeInactive: true });
    const idSet = new Set(params.ids.map(String));
    return { data: all.filter((o) => idSet.has(String(o.id))) };
  },

  getManyReference: async (params: {
    pagination: { page: number; perPage: number };
    sort: { field: string; order: 'ASC' | 'DESC' };
    filter: Record<string, any>;
  }) => {
    const all = await fetchCustomizationOptions({
      includeInactive: params.filter?.includeInactive === true,
    });
    return sortAndPaginate(all, params.sort, params.pagination);
  },

  create: async (params: { data: Record<string, unknown> }) => {
    if (hasCustomizationOptionImageUpload(params.data)) {
      const json = await submitFormData({
        data: params.data,
        resource: CUSTOMIZATION_OPTIONS_RESOURCE,
        method: 'create',
        fileFields: [{ sourceKey: 'image', formDataKey: 'image', existingKey: 'existingImage', multiple: false }],
        arrayFields: ['labelIds'],
        objectFields: ['translations'],
      });
      return { data: transformCustomizationOption(json as unknown as CustomizationOption) };
    }
    const { json } = await httpClient(`${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}`, {
      method: 'POST',
      body: JSON.stringify(params.data),
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    });
    return { data: transformCustomizationOption(json as CustomizationOption) };
  },

  update: async (params: { id: string | number; data: Record<string, unknown> }) => {
    const { id: _id, labels: _labels, createdAt: _c, updatedAt: _u, deletedAt: _d, ...patchBody } = params.data;
    if (hasCustomizationOptionImageUpload(patchBody) || patchBody.removePhoto === 'true') {
      const json = await submitFormData({
        data: patchBody,
        resource: CUSTOMIZATION_OPTIONS_RESOURCE,
        method: 'update',
        id: params.id,
        fileFields: [{ sourceKey: 'image', formDataKey: 'image', existingKey: 'existingImage', multiple: false }],
        arrayFields: ['labelIds'],
        objectFields: ['translations'],
        updateHttpMethod: 'PATCH',
      });
      return { data: transformCustomizationOption(json as unknown as CustomizationOption) };
    }
    const { json } = await httpClient(`${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}/${params.id}`, {
      method: 'PATCH',
      body: JSON.stringify(patchBody),
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    });
    return { data: transformCustomizationOption(json as CustomizationOption) };
  },

  updateMany: async (params: { ids: Array<string | number>; data: Record<string, unknown> }) => {
    const responses = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(params.data),
          headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
        }),
      ),
    );
    return { data: responses.map((r) => (r.json as { id: string | number }).id) };
  },

  delete: async (params: { id: string | number }) => {
    const { json } = await httpClient(`${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}/${params.id}`, {
      method: 'DELETE',
    });
    return { data: json };
  },

  deleteMany: async (params: { ids: Array<string | number> }) => {
    const responses = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${CUSTOMIZATION_OPTIONS_RESOURCE}/${id}`, { method: 'DELETE' }),
      ),
    );
    return { data: responses.map((r) => (r.json as { id: string | number })?.id ?? '') };
  },
};

// Remap the admin-panel-facing resource name to the backend URL path.
// Keeps the React-Admin resource name clean ("delivery-time-slots",
// "attribute-definitions") while hitting the admin-scoped backend route
// (e.g. `/api/admin/delivery-time-slots`, `/api/admin/product-attribute-definitions`).
const remapResource = (resource: string): string => {
  if (resource === DELIVERY_TIME_SLOTS_RESOURCE) return DELIVERY_TIME_SLOTS_BACKEND;
  if (resource === ATTRIBUTE_DEFINITIONS_RESOURCE) return ATTRIBUTE_DEFINITIONS_BACKEND;
  return resource;
};

const dataProvider = {
  ...backendRestProvider,

  getList: (resource: string, params: any) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.getList(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.getList(params);
    }
    if (resource === 'product-compositions') {
      const savedBranchId = localStorage.getItem('selectedBranchId');
      const branchId = savedBranchId && savedBranchId !== 'all' ? savedBranchId : null;
      const merged = branchId
        ? { ...params, filter: { ...(params?.filter ?? {}), branchId } }
        : params;
      return backendRestProvider.getList('product-compositions', merged);
    }
    return backendRestProvider.getList(remapResource(resource), params);
  },

  getMany: (resource: string, params: any) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.getMany(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.getMany(params);
    }
    return backendRestProvider.getMany(remapResource(resource), params);
  },

  getManyReference: (resource: string, params: any) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.getManyReference(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.getManyReference(params);
    }
    return backendRestProvider.getManyReference(remapResource(resource), params);
  },

  updateMany: (resource: string, params: any) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.updateMany(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.updateMany(params);
    }
    return backendRestProvider.updateMany(remapResource(resource), params);
  },

  delete: (resource: string, params: any) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.delete(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.delete(params);
    }
    return backendRestProvider.delete(remapResource(resource), params);
  },

  deleteMany: (resource: string, params: any) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.deleteMany(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.deleteMany(params);
    }
    return backendRestProvider.deleteMany(remapResource(resource), params);
  },

  getOne: (resource: string, params: { id: string | number }) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.getOne(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.getOne(params);
    }
    if (resource === DELIVERY_TIME_SLOTS_RESOURCE) {
      return backendRestProvider.getOne(DELIVERY_TIME_SLOTS_BACKEND, params);
    }
    if (resource === ATTRIBUTE_DEFINITIONS_RESOURCE) {
      return backendRestProvider.getOne(ATTRIBUTE_DEFINITIONS_BACKEND, params);
    }
    if (resource === 'products') {
      const savedBranchId = localStorage.getItem('selectedBranchId');
      const branchId = savedBranchId && savedBranchId !== 'all' ? savedBranchId : null;
      const url = branchId
        ? `${API_URL}/products/${params.id}?branchId=${branchId}`
        : `${API_URL}/products/${params.id}`;
      return httpClient(url).then(({ json }) => ({ data: json }));
    }
    if (resource === 'product-compositions') {
      const savedBranchId = localStorage.getItem('selectedBranchId');
      const branchId = savedBranchId && savedBranchId !== 'all' ? savedBranchId : null;
      const url = branchId
        ? `${API_URL}/product-compositions/${params.id}?branchId=${branchId}`
        : `${API_URL}/product-compositions/${params.id}`;
      return httpClient(url).then(({ json }) => ({ data: transformPhotosForEdit(json) }));
    }

    return backendRestProvider.getOne(resource, params).then((result) => {
      if (attachmentBasedResources.includes(resource)) {
        return { ...result, data: transformAttachmentsForEdit(result.data) };
      }
      if (photosBasedResources.includes(resource)) {
        return { ...result, data: transformPhotosForEdit(result.data) };
      }
      return result;
    });
  },

  create: (resource: string, params: { data: Record<string, unknown> }) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.create(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.create(params);
    }
    if (resource === CAMPAIGNS_RESOURCE) {
      return createCampaign(params.data).then((data) => ({ data }));
    }
    if (imgBasedResources.includes(resource)) {
      return uploadImage(params.data, resource, 'create').then((data) => ({ data }));
    }
    if (attachmentBasedResources.includes(resource)) {
      return uploadWithAttachments(params.data, resource, 'create').then((data) => ({ data }));
    }
    if (photosBasedResources.includes(resource)) {
      return uploadWithPhotos(params.data, resource, 'create').then((data) => ({ data }));
    }
    return backendRestProvider.create(remapResource(resource), params);
  },

  update: (resource: string, params: { id: string | number; data: Record<string, unknown>; previousData: Record<string, unknown> }) => {
    if (resource === CATALOG_LABELS_RESOURCE) {
      return catalogLabelsProvider.update(params);
    }
    if (resource === CUSTOMIZATION_OPTIONS_RESOURCE) {
      return customizationOptionsProvider.update(params);
    }
    if (resource === CAMPAIGNS_RESOURCE) {
      return updateCampaign(params.id, params.data).then((data) => ({ data }));
    }
    if (imgBasedResources.includes(resource)) {
      return uploadImage(params.data, resource, 'update', params.id).then((data) => ({ data }));
    }
    if (attachmentBasedResources.includes(resource)) {
      return uploadWithAttachments(params.data, resource, 'update', params.id).then((data) => ({ data }));
    }
    if (photosBasedResources.includes(resource)) {
      return uploadWithPhotos(params.data, resource, 'update', params.id).then((data) => ({ data }));
    }
    return backendRestProvider.update(remapResource(resource), params);
  },
};

export default dataProvider as DataProvider;
